import * as globalTypes from "Store/global/constants";
import { initialPagination } from "utils/initialObjects";

/* Constant required for resetting pagination */
const PAGINATION_INIT = {
  lessons: { ...initialPagination, sort_by: "page_number" },
  courseTemplates: {
    ...initialPagination,
    template_type: "built_in,organization",
    stats: 1
  },
  tests: initialPagination,
  worksheets: initialPagination,
  studentLessons: { ...initialPagination /* sort_by: "page_number" */ },
  studentWorksheets: initialPagination,
  studentTests: { ...initialPagination, sort_by: "name", sort_type: "desc" },
  studentScoredTests: initialPagination,
  classroomLessons: initialPagination,
  classroomWorksheets: initialPagination,
  classroomTests: initialPagination,
  students: { ...initialPagination, sort_by: "name", sort_type: "asc" },
  locations: initialPagination,
  classrooms: initialPagination,
  instructors: initialPagination,
  instructorStudents: { ...initialPagination, sort_by: "name", sort_type: "asc" },
  classroomStudents: initialPagination,
  worksheetsByCategory: initialPagination
};

const initialState: IGlobalReducer = {
  actionSpinner: false,
  activeCourse: null,
  classroomLessonsPagination: PAGINATION_INIT.classroomLessons,
  classroomTestsPagination: PAGINATION_INIT.classroomWorksheets,
  classroomWorksheetsPagination: PAGINATION_INIT.classroomWorksheets,
  classroomsPagination: PAGINATION_INIT.classrooms,
  classroomsSimplified: null,
  classroomStudentsPagination: PAGINATION_INIT.classroomStudents,
  categoriesTree: [],
  courseTemplatesPagination: PAGINATION_INIT.courseTemplates,
  courses: [],
  errorSignal: false,
  fetchCalendarSpinner: false,
  fetchDetailsSpinner: false,
  fetchSpinner: false,
  fetchSublistSpinner: false,
  globalSpinner: false,
  reportSpinner: false,
  instructorsPagination: PAGINATION_INIT.instructors,
  instructorsSimplified: null,
  instructorStudentsPagination: PAGINATION_INIT.instructorStudents,
  lessonTemplatePreview: null,
  lessonsFilterItems: null,
  lessonsHints: [],
  lessonsPagination: PAGINATION_INIT.lessons,
  lessonsTemplates: [],
  locationsPagination: PAGINATION_INIT.locations,
  locationsSimplified: null,
  studentLessonsPagination: PAGINATION_INIT.studentLessons,
  studentScoredTestsPagination: PAGINATION_INIT.studentScoredTests,
  studentTestsPagination: PAGINATION_INIT.studentTests,
  studentWorksheetsPagination: PAGINATION_INIT.studentWorksheets,
  studentsPagination: PAGINATION_INIT.students,
  worksheetsByCategoryPagination: PAGINATION_INIT.worksheetsByCategory,
  studentsSimplified: [],
  subjects: null,
  successMessage: "",
  templateLoader: false,
  testsPagination: PAGINATION_INIT.tests,
  testsTemplates: [],
  unitFilterOptions: [],
  worksheetTemplatePreview: null,
  worksheetsFilterItems: null,
  worksheetsHints: [],
  worksheetsPagination: PAGINATION_INIT.worksheets,
  worksheetsTemplates: [],
  worksheetsByCategory: []
};

const handleActions = (state, action) => {
  switch (action.type) {
    case globalTypes.RESET_ALL_MESSAGES:
      return {
        successMessage: ""
      };
    case globalTypes.SET_TESTS_TEMPLATES:
      return {
        testsTemplates: [...action.payload]
      };
    case globalTypes.SET_LESSONS_TEMPLATES:
      return {
        lessonsTemplates: [...action.payload]
      };
    case globalTypes.SET_WORKSHEETS_TEMPLATES:
      return {
        worksheetsTemplates: [...action.payload]
      };
    case globalTypes.SET_PAGINATION:
      return {
        [`${action.paginationType}Pagination`]: {
          ...(action.reset
            ? PAGINATION_INIT[action.paginationType]
            : state[`${action.paginationType}Pagination`]),
          ...action.payload
        }
      };
    case globalTypes.RESET_PAGINATION:
      return {
        [`${action.paginationType}Pagination`]: PAGINATION_INIT[action.paginationType]
      };
    case globalTypes.SET_GLOBAL_TEMPLATE_LOADER:
      return {
        templateLoader: action.payload
      };
    case globalTypes.SET_WORKSHEETS_FILTER_ITEMS:
      return {
        worksheetsFilterItems: action.payload
      };
    case globalTypes.SET_LESSONS_FILTER_ITEMS:
      return {
        lessonsFilterItems: action.payload
      };
    case globalTypes.SET_ERROR_SIGNAL:
      return {
        errorSignal: action.payload
      };
    case globalTypes.SET_SUBJECTS:
      return {
        subjects: action.payload
      };
    case globalTypes.SET_ACTION_SPINNER:
      return {
        actionSpinner: action.payload
      };
    case globalTypes.SET_GLOBAL_SPINNER:
      return {
        globalSpinner: action.payload
      };
    case globalTypes.SET_FETCH_SPINNER:
      return {
        fetchSpinner: action.payload
      };
    case globalTypes.SET_FETCH_CALENDAR_SPINNER:
      return {
        fetchCalendarSpinner: action.payload
      };
    case globalTypes.SET_FETCH_DETAILS_SPINNER:
      return {
        fetchDetailsSpinner: action.payload
      };
    case globalTypes.SET_FETCH_SUBLIST_SPINNER:
      return {
        fetchSublistSpinner: action.payload
      };

    case globalTypes.SET_LESSONS_HINTS:
      return {
        lessonsHints: action.payload
      };
    case globalTypes.SET_WORKSHEETS_HINTS:
      return {
        worksheetsHints: action.payload
      };
    case globalTypes.SET_UNIT_FILTER_OPTIONS:
      return {
        unitFilterOptions: action.payload
      };

    case globalTypes.SET_CLASSROOMS_SIMPLIFIED:
      return {
        classroomsSimplified: [...action.payload]
      };
    case globalTypes.SET_INSTRUCTORS_SIMPLIFIED:
      return {
        instructorsSimplified: [...action.payload]
      };
    case globalTypes.SET_LOCATIONS_SIMPLIFIED:
      return {
        locationsSimplified: [...action.payload]
      };
    case globalTypes.SET_COURSES:
      return {
        courses: action.payload
      };
    case globalTypes.SET_ACTIVE_COURSE:
      return {
        activeCourse: action.payload
      };
    case globalTypes.SET_LESSON_TEMPLATE_PREVIEW:
      return {
        lessonTemplatePreview: action.payload
      };
    case globalTypes.SET_WORKSHEET_TEMPLATE_PREVIEW:
      return {
        worksheetTemplatePreview: action.payload
      };
    case globalTypes.SET_CATEGORY_TREE:
      return {
        categoriesTree: action.payload
      };

    case globalTypes.SET_STUDENTS_SIMPLIFIED:
      return {
        studentsSimplified: action.payload
      };
    case globalTypes.SET_WORKSHEETS_BY_CATEGORY:
      return {
        worksheetsByCategory: action.payload
      };
    case globalTypes.SET_REPORT_SPINNER:
      return {
        reportSpinner: action.payload
      };
    default:
      return {};
  }
};

const globalReducer = (state: IGlobalReducer = initialState, action) => ({
  ...state,
  ...handleActions(state, action)
});

export default globalReducer;
